<template>
  <v-row>
    <v-col lg="3" md="4" sm="12" cols="12">
      <v-row>
        <v-col order="2" order-md="1" md="12" sm="6" cols="12">
          <!-- Profile Information -->
          <v-card id="profile-card" class="pa-4" width="100%" height="100%">
            <div class="d-flex justify-center" :class="{ 'float-left': $vuetify.breakpoint.xs }">
              <profile-avatar
                class="elevation-5 mx-auto"
                :class="{
                  'mt-2': !$vuetify.breakpoint.xs,
                  'mt-0': $vuetify.breakpoint.xs,
                }"
                :size="$vuetify.breakpoint.xs ? 48 : 150"
                :username="profileName"
                :preset-profile-pic="profile.picture"
              />
            </div>

            <div
              class="px-4"
              :class="{
                'ml-12': $vuetify.breakpoint.xs,
                'mb-2': $vuetify.breakpoint.xs,
              }"
            >
              <v-card-title
                v-if="profile.first"
                :key="profile.first"
                class="text-h5 pa-0 font-weight-medium"
                style="word-break: break-word"
              >
                {{ profile.first ? profile.first + " " + (profile.last || "") : profileName }}
              </v-card-title>
              <v-card-title v-else class="text-h5 pa-0 font-weight-medium" style="word-break: break-word">
                {{ profileName }}
              </v-card-title>
              <v-card-subtitle class="pa-0"> @{{ profileName.toLowerCase() }} </v-card-subtitle>
            </div>

            <div class="my-1 mx-0 mx-sm-4">
              <button
                @click="
                  () => {
                    workEngagementsSummary.expanded = !workEngagementsSummary.expanded;
                  }
                "
              >
                <b>{{ workEngagementsSummary.overall.total }}</b>
                <span class="work-engagement-label"> works engaged, </span>
                <b>
                  {{ workEngagementsSummary.thisYear.total }}
                </b>
                <span class="work-engagement-label">this year</span>
                <v-icon v-if="!workEngagementsSummary.expanded">mdi-chevron-down</v-icon>
                <v-icon v-else>mdi-chevron-up</v-icon>
              </button>
              <div v-if="workEngagementsSummary.expanded">
                <div v-for="medium in Object.values($mediums)" :key="medium" class="work-engagement-link">
                  <router-link :to="{ name: 'works-engaged', query: { medium } }">
                    <b>{{ workEngagementsSummary.overall[medium] }}</b>
                    <span class="work-engagement-label">
                      {{ userFriendlyMediumWord(medium, true).toLowerCase() }}
                      {{ mediumSpecificVerbForEngaged(medium).toLowerCase() }}
                      ,</span
                    >
                    <b>
                      {{ workEngagementsSummary.thisYear[medium] }}
                    </b>
                    <span class="work-engagement-label">this year</span>
                  </router-link>
                </div>
              </div>
            </div>

            <div v-if="$vuetify.breakpoint.xs">
              <v-divider class="mb-2"></v-divider>

              <profile-utility-buttons
                v-bind="profileUtilityProps"
                v-on="profileUtilityEventHandlers"
                class="pl-1"
              ></profile-utility-buttons>
            </div>
          </v-card>
        </v-col>

        <v-col
          v-if="friendRequestStatus === 'incoming'"
          order="1"
          order-md="2"
          cols="12"
          md=""
          class="friend-request-tile"
        >
          <v-card class="px-5">
            <v-card-text class="pb-0 pl-0">{{ profileName }} wants to be your friend!</v-card-text>
            <v-card-actions class="pb-4 px-0">
              <v-btn
                @click="
                  addFriendById(profile.id);
                  friendRequestStatus = 'approved';
                "
                color="primary"
                depressed
                >Accept</v-btn
              >
              <v-spacer></v-spacer>
              <v-btn
                @click="
                  removeFriendById(profile.id);
                  friendRequestStatus = null;
                "
                outlined
                >Decline</v-btn
              >
            </v-card-actions>
          </v-card>
        </v-col>

        <v-col order="3" v-if="!$vuetify.breakpoint.xs" md="12" sm="6" xs="12">
          <!-- Profile Menu -->
          <v-card class="px-5 py-3" height="100%">
            <profile-utility-buttons
              v-bind="profileUtilityProps"
              v-on="profileUtilityEventHandlers"
            ></profile-utility-buttons>
          </v-card>
        </v-col>
      </v-row>

      <v-row v-if="(isMyProfile || hasFavorites) && !profileIsBlockedByUser">
        <v-col cols="12">
          <!-- Favorites -->
          <v-card class="py-2">
            <v-card-title class="text-h5 justify-center"> Favorites </v-card-title>

            <v-card-text v-if="loading.favorites" class="text-center">
              <v-progress-circular indeterminate color="primary" />
            </v-card-text>

            <v-card-text v-else-if="hasFavorites">
              <v-row class="profile-favorites" justify="center" align="baseline">
                <v-col
                  v-for="medium in sortedFavoritesByAspect.slice(
                    0,
                    sortedFavoritesByAspect.length === 4 ? 2 : sortedFavoritesByAspect.length,
                  )"
                  :key="medium.id"
                  cols="4"
                >
                  <favorite-tile :key="medium.id" :data="medium" width="100%" />
                </v-col>
              </v-row>
              <v-row
                v-if="sortedFavoritesByAspect.length === 4"
                class="profile-favorites"
                justify="center"
                align="start"
              >
                <v-col
                  v-for="medium in sortedFavoritesByAspect.slice(2, sortedFavoritesByAspect.length)"
                  :key="medium.id"
                  cols="4"
                >
                  <favorite-tile :key="medium.id" :data="medium" width="100%" />
                </v-col>
              </v-row>
            </v-card-text>

            <v-card-text v-else-if="isMyProfile" class="px-10 pt-2 pb-5 text-center">
              You don't seem to have any favorites set yet. Click on the edit button above to add a new favorite!
            </v-card-text>

            <v-card-actions v-if="isMyProfile" class="edit-favorites">
              <v-btn icon large @click="displayEditFavoritesDrawer">
                <v-icon color="text lighten-2"> mdi-pencil </v-icon>
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </v-col>

    <v-col lg="9" md="8" sm="12" xs="12">
      <v-card v-if="!profileIsBlockedByUser" class="pa-2">
        <v-row align="center">
          <v-col cols="8">
            <v-card-title class="text-h4"> Reviews </v-card-title>
          </v-col>

          <v-col cols="4">
            <v-select
              v-model="sortBy"
              outlined
              dense
              clearable
              class="mr-4 mb-1"
              placeholder="Sort"
              hide-details="auto"
              :items="sortOptions"
              :menu-props="{ bottom: true, offsetY: true }"
              @change="resetReviews"
              color="secondary"
              item-color="secondary"
            />

            <v-select
              v-model="filterItem"
              outlined
              dense
              clearable
              class="mr-4"
              placeholder="Filter"
              hide-details="auto"
              :items="filterReviewsOptions"
              :menu-props="{ bottom: true, offsetY: true }"
              @change="filterReviews"
              color="secondary"
              item-color="secondary"
            />
          </v-col>
        </v-row>

        <v-row>
          <v-col>
            <v-card v-if="loading.reviews && !hasReviews" class="pb-5 text-center" flat>
              <v-progress-circular indeterminate color="primary" />
            </v-card>

            <infinite-feed
              v-else-if="hasReviews"
              :enabled="!endOfReviews && !loading.reviews"
              @load-more="handleLoadMore"
            >
              <waterfall id="profile-reviews" ref="profile-reviews" :options="{ gutter: 20, fitWidth: true }">
                <waterfall-item v-for="review in reviews" :key="review.id" :data="review">
                  <review-tile-profile-feed :data="review" type="individual" />
                </waterfall-item>
              </waterfall>
            </infinite-feed>

            <v-card v-else-if="isMyProfile" class="pb-5" flat>
              <v-card-title class="pt-0 text-h5 justify-center"> No Reviews </v-card-title>

              <v-card-text class="text-center">
                It seems that you haven't posted any reviews yet.<br />Why not create one?
              </v-card-text>

              <v-card-actions class="justify-center">
                <v-btn large color="primary" dark :to="{ name: 'rate' }"> Create A Rating </v-btn>
              </v-card-actions>
            </v-card>

            <v-card v-else flat>
              <v-card-title class="pt-0 text-h5 justify-center"> No Reviews </v-card-title>

              <v-card-subtitle class="text-center">
                It seems that {{ profileName }} hasn't posted any reviews yet.
              </v-card-subtitle>
            </v-card>
          </v-col>
        </v-row>
      </v-card>

      <v-card v-else>
        <v-card-title class="justify-center">User is blocked</v-card-title>
      </v-card>
    </v-col>
  </v-row>
</template>

<script>
import { Waterfall, WaterfallItem } from "vue2-waterfall";
import { mapMutations } from "vuex";

import getAspectRatio from "../util/getAspectRatio";
import deleteReview from "../mixins/deleteReview.js";
import Friends from "../mixins/friends";
import RouterHelpers from "../mixins/routerHelpers";

import profileUtilityButtons from "../components/profile-utility-buttons/profile-utility-buttons.vue";
import infiniteFeed from "../components/infinite-feed.vue";
import favoriteTile from "../components/favorite-tile.vue";

import { mediums } from "../util/enums";
import { mediumSpecificVerbForEngaged, userFriendlyMediumWord } from "../util/mediumHelpers";

export default {
  name: "ProfilePage",

  components: { Waterfall, WaterfallItem, profileUtilityButtons, infiniteFeed, favoriteTile },

  mixins: [deleteReview, Friends, RouterHelpers],

  data: () => ({
    reviews: [],
    reviewsStartIndex: 0,
    reviewsLimit: 20,
    endOfReviews: false,

    filters: {
      album: true,
      podcast: true,
      film: true,
      book: true,
      tvShow: true,
    },

    filterItem: null,
    // https://v2.vuetifyjs.com/en/api/v-select/#props-items
    filterReviewsOptions: Object.values(mediums).map((medium) => ({
      text: userFriendlyMediumWord(medium, true),
      value: medium,
    })),
    sortBy: null,
    sortOptions: ["rating"],

    loading: {
      reviews: true,
      favorites: true,
    },

    favorites: {
      album: { id: "" },
      podcast: { id: "" },
      film: { id: "" },
      book: { id: "" },
      tvShow: { id: "" },
    },

    friendRequestStatus: "Loading",

    profile: {
      first: "",
      last: "",
      username: "",
      id: undefined,
      picture: undefined,
    },

    profileIsBlockedByUser: false,
    userIsBlockedByProfile: false,

    workEngagementsSummary: {
      expanded: false,
      overall: {
        total: "?",
        // TODO: Include different mediums
      },
      thisYear: {
        total: "?",
      },
    },
  }),

  computed: {
    // name of profile page
    profileName: function () {
      return this.$route.params.username.toString();
    },

    // name of current user
    username: function () {
      return this.$store.state.username;
    },

    isLoggedIn: function () {
      return !!this.username;
    },

    // check if profile is the user's
    isMyProfile: function () {
      return this.profileName === this.username;
    },

    hasFavorites: function () {
      for (let medium in this.favorites) {
        if (this.favorites[medium].id) return true;
      }

      return false;
    },

    hasReviews: function () {
      return this.reviews.length > 0;
    },

    profileUtilityProps: function () {
      return {
        "profile-username": this.profileName,
        "profile-id": this.profile.id,
        "profile-is-friend": this.friendRequestStatus === "approved",
        "profile-is-blocked-by-user": this.profileIsBlockedByUser,
      };
    },

    profileUtilityEventHandlers: function () {
      return {
        "add-friend": ($event) => {
          this.friendRequestStatus = $event.requestStatus;
        },
        "remove-friend": () => {
          this.friendRequestStatus = null;
        },
        "block-user": () => {
          this.profileIsBlockedByUser = true;
          this.friendRequestStatus = null;
        },
        "unblock-user": () => {
          this.profileIsBlockedByUser = false;
        },
        "report-user": () => {
          this.showReportPopup({
            contendId: this.profile.id,
            contentType: "user",
          });
        },
      };
    },

    // Used for better aesthetics. Ensures that mediums with same height are likely on same row
    sortedFavoritesByAspect() {
      const unsortedFavorites = Object.values(this.favorites).filter((item) => item.id);

      // Count 1:1 and 2:3
      const squarePosterFavorites = unsortedFavorites.filter((item) => {
        return getAspectRatio(item.medium) === 1;
      });
      const rectanglePosterFavorites = unsortedFavorites.filter((item) => {
        return getAspectRatio(item.medium) === 2 / 3;
      });
      return rectanglePosterFavorites.concat(squarePosterFavorites);
    },
  },

  watch: {
    profileName: async function () {
      await this.getProfile();

      await this.loadFriendRequestStatus();

      await this.resetReviews();

      for (let medium of Object.values(this.$mediums)) {
        await this.getFavorite(medium);
      }

      this.workEngagementsSummary.overall = { total: "?" };
      this.workEngagementsSummary.thisYear = { total: "?" };
      await this.getWorkEngagementsSummary();
    },
  },

  mounted: async function () {
    if (this.isLoggedIn) {
      await this.getBlockedStatus();
      if (this.userIsBlockedByProfile) {
        await this.redirectTo404();
        return;
      }
    }

    await this.getProfile();

    if (this.isLoggedIn) {
      await this.loadFriendRequestStatus();
    }

    // Favorites need to be loaded before reviews to prevent them appearing right as a user tries to click on a review
    for (let medium of Object.values(this.$mediums)) {
      await this.getFavorite(medium);
    }

    await this.getReviews();
    await this.getWorkEngagementsSummary();

    // Event listeners

    this.$root.$on("update-favorites", (medium) => {
      this.getFavorite(medium);
    });

    this.$root.$on("delete-review", (id) => {
      let deleteIdx = this.reviews.findIndex((review) => review.id === id);
      this.reviews.splice(deleteIdx, 1);
    });
  },

  methods: {
    ...mapMutations(["showSnackbar", "closeSnackbar"]),
    ...mapMutations(["showDrawer", "closeDrawer"]),
    ...mapMutations(["showReportPopup"]),
    userFriendlyMediumWord,
    mediumSpecificVerbForEngaged,

    handleLoadMore: async function () {
      let newEntries = await this.getReviews();
      if (newEntries.length < this.reviewsLimit) this.endOfReviews = true;
    },

    displayEditFavoritesDrawer() {
      this.showDrawer({
        status: "edit-favorites",
        data: {
          album: this.favorites.album.id,
          podcast: this.favorites.podcast.id,
          film: this.favorites.film.id,
          book: this.favorites.book.id,
          tvShow: this.favorites.tvShow.id,
        },
      });
    },

    getFavorite: async function (medium) {
      this.loading.favorites = true;
      await this.$axios
        .get("/favorites", {
          params: {
            profile: this.profileName,
            medium: medium,
          },
        })
        .then((response) => {
          this.favorites[medium] = {
            medium: medium,
            poster_source: response.data.posterSource,
            title: response.data.title,
            id: response.data.internal_id,
          };
        })
        .catch(() => {
          this.favorites[medium] = {
            medium: medium,
            poster_source: "",
            title: "",
            id: "",
          };
        });
      this.loading.favorites = false;
    },

    getProfile: async function () {
      const response = await this.$axios.get("/profile", {
        params: {
          user: this.profileName,
        },
      });

      this.profile.first = response.data.first_name;
      this.profile.last = response.data.last_name;
      this.profile.username = this.profileName;
      this.profile.id = response.data.id;
      this.profile.picture = response.data.profile_pic;
    },

    getReviews: async function () {
      this.loading.reviews = true;

      // convert this.filters object to an array of mediums where value set to true
      let filters = Object.keys(this.filters).filter((key) => this.filters[key]);
      let params = {
        author: this.profile.id,
        mediums: filters ? filters : undefined,
        limit: this.reviewsLimit,
        offset: this.reviewsStartIndex,
      };
      if (this.sortBy) {
        params.sort = [
          {
            sortBy: this.sortBy,
            order: "DESCENDING",
          },
        ];
      }

      const response = await this.$axios.get("/v1/reviews", {
        params,
      });
      const reviews = response.data.items;
      this.reviews = this.reviews.concat(reviews);
      this.reviewsStartIndex += this.reviewsLimit;

      this.loading.reviews = false;
      return response.data;
    },

    resetReviews: function () {
      this.reviews = [];
      this.reviewsStartIndex = 0;
      this.getReviews();
    },

    loadFriendRequestStatus: async function () {
      if (this.username === this.profileName) return;
      this.friendRequestStatus = await this.getFriendshipStatus(this.profile.id);
    },

    getBlockedStatus: async function () {
      const response = await this.$axios.get("/blocked-status", {
        params: {
          user: this.profileName,
        },
      });
      this.profileIsBlockedByUser = response.data === "Blocked by you";
      this.userIsBlockedByProfile = response.data === "Blocked by them";
    },

    filterReviews: function () {
      if (this.filterItem) {
        for (const medium of Object.keys(this.filters)) this.filters[medium] = false;
        this.filters[this.filterItem] = true;
      } else {
        for (let medium in this.filters) this.filters[medium] = true;
      }

      // Reset reviews (easier for combining filtering and infinite scrolling)
      this.resetReviews();
    },

    async getWorkEngagementsSummary() {
      const overallResponse = await this.$axios.get(`/v1/work-engagements/summary/${this.profile.id}`);
      const minDateEngaged = new Date();
      minDateEngaged.setDate(1);
      minDateEngaged.setMonth(0);
      const thisYearResponse = await this.$axios.get(`/v1/reviews/summary/${this.profile.id}`, {
        params: {
          minDateEngaged: minDateEngaged.toISOString(),
        },
      });
      this.workEngagementsSummary.overall = overallResponse.data;
      this.workEngagementsSummary.thisYear = thisYearResponse.data.counts;
    },
  },
};
</script>

<style scoped>
.waterfall {
  margin: 0 auto;
}

.Waterfall-item {
  margin-bottom: 20px;
}

.friend-request-tile .v-card__text {
  font-size: 1rem;
  color: var(--v-text-base) !important;
}

.edit-favorites {
  position: absolute;

  top: 0;
  right: 0;

  margin-top: 10px;
}

.text-h5 {
  line-height: 32px;
}

.v-card__subtitle {
  position: relative;
  margin-top: -6px !important;
}

@media (max-width: 600px) {
  .v-list-item {
    margin-bottom: 0 !important;
  }
}

.work-engagement-label {
  color: var(--v-text-lighten1);
  font-size: 14px;
}

.work-engagement-link {
  * {
    text-decoration-color: var(--v-text-lighten1) !important;
  }

  b {
    color: var(--v-text-base);
  }

  :hover {
    text-decoration: underline;
  }
}
</style>
